'use client';

import '@actr/utilities/src/polyfills';

import mainContainer, { initDI, loadDIModules, useInject } from '@actr/di';
import activityDIModule from '@actr/stores/src/modules/account/activity';
import findMyOrderDIModule from '@actr/stores/src/modules/account/find-my-order';
import appDIModule from '@actr/stores/src/modules/common/app';
import channelConfigDIModule from '@actr/stores/src/modules/common/channel-config';
import userInfoDIModule from '@actr/stores/src/modules/common/user-info';
import { AppStore } from '@actr/stores/src/stores/stores/common/app';
import { AKVanillaThemeProvider } from '@actr/style/src/providers/vanilla-theme-provider';
import { DeviceSizes } from '@actr/style/src/specs';
import { UiContext } from '@actr/ui/src/contexts/ui-context';
import { canUseDom } from '@actr/utilities/src/dom';
import { getStorage, STORAGES } from '@actr/utilities/src/local-storage';
import { saveAdsData } from '@actr/utilities/src/url/ad-checker';
import { saveAdmitad } from '@actr/utilities/src/url/admitad';
import { saveClickId } from '@actr/utilities/src/url/click-id';
import CookieNotifier from '@actr/widgets/src/components/_common/cookie-notifier';
import OpenInAppPanel from '@actr/widgets/src/components/_common/open-in-app-panel';
import { AirlineProvider } from '@actr/widgets/src/components/_misc/app-installation-buttons/context';
import { ErrorBoundary } from '@actr/widgets/src/components/_misc/error-boundary';
import { StickyRightButtons } from '@actr/widgets/src/components/_misc/sticky-right-buttons';
import { FeedbackPanel } from '@actr/widgets/src/components/feedback/panel';
import { WidgetsContext } from '@actr/widgets/src/contexts/widgets';
import { useAuthSession } from '@actr/widgets/src/hooks/use-auth-session';
import { UseFetchChannelConfig } from '@actr/widgets/src/hooks/use-fetch-channel-config';
import { observer } from 'mobx-react-lite';
import { PropsWithChildren, useCallback, useEffect } from 'react';

import config from '@/configs';
import ContentWrapper from '@/hoc/content-wrapper';
import localAuthDIModule from '@/modules/override/auth';
import OptionalMetricsConnection from '@/utils/metrica/optional-metrics-connection';

import Footer from '../footer';
import Header from '../header';
import * as Styled from './style.css';

const AppWrapper: React.FC<PropsWithChildren> = observer(({ children }) => {
  const app = useInject(AppStore);

  useEffect(() => {
    app.setTheme(config.theme);

    if (canUseDom) {
      saveAdmitad();
      saveAdsData();
      saveClickId();
    }
  }, []);

  useEffect(() => {
    if (getStorage('cookieAccepted', [STORAGES.COOKIE])) {
      app.setIsCookieAccepted(true);
    }
  }, [getStorage('cookieAccepted', [STORAGES.COOKIE])]);

  UseFetchChannelConfig();
  useAuthSession();

  const handleUpdateDeviceSize = useCallback((deviceSize: DeviceSizes) => {
    app.setDeviceSize(deviceSize);
  }, []);

  const handleError = useCallback((error: Error | null) => {
    if (error) {
      app.addError(error);
    }
  }, []);

  function renderContent() {
    return (
      <>
        <Header />
        {canUseDom && <OptionalMetricsConnection />}
        <div className={Styled.MainContent} id="siteMainContent">
          { children }
        </div>
        <Footer />
        <StickyRightButtons />
        {canUseDom && <FeedbackPanel />}
        {canUseDom && <OpenInAppPanel />}
        {!app.isCookieAccepted && (
        <CookieNotifier isNeedAdditionalBottom={false} />
        )}
      </>
    );
  }

  return (
    <div className={Styled.RootWrapper} id={config.rootId}>
      <WidgetsContext.Provider value={{
        excludedPayments: [],
        projectType: config.projectType
      }}>
        <UiContext.Provider value={{
          deviceSize: app.deviceSize,
          popoverContainerElement: canUseDom ? (document.getElementById('popover-container') || document.body) : null,
          modalContainerElement: canUseDom ? (document.getElementById('modal-container') || document.body) : null,
        }}>
          <AirlineProvider>
            <AKVanillaThemeProvider currentTheme={config.theme} onUpdateDeviceSize={handleUpdateDeviceSize}>
              <ContentWrapper>
                <ErrorBoundary local={false} onReactError={handleError}>
                  { renderContent() }
                </ErrorBoundary>
              </ContentWrapper>
            </AKVanillaThemeProvider>
          </AirlineProvider>
        </UiContext.Provider>
      </WidgetsContext.Provider>
    </div>
  );
});

export default initDI(loadDIModules(
  AppWrapper,
  appDIModule,
  localAuthDIModule,
  activityDIModule,
  channelConfigDIModule,
  userInfoDIModule,
  findMyOrderDIModule
), mainContainer);
